import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import { fadeUp } from '../utils/animations';
import Section from './Section';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const { FiStar, FiTrendingUp, FiUsers, FiMessageSquare, FiPieChart, FiBarChart2, FiSmile } = FiIcons;

const KEYWORDS = [
  { text: "Transformative", weight: "high" },
  { text: "Actionable", weight: "high" },
  { text: "Engaging", weight: "medium" },
  { text: "Visionary", weight: "medium" },
  { text: "Inspiring", weight: "medium" },
  { text: "Practical", weight: "low" },
  { text: "Human-Centered", weight: "low" },
];

export default function InteractionsFeedbackAnalytics() {
  const [feedbackData, setFeedbackData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchFeedback() {
      try {
        const { data, error } = await supabase
          .from('speaking_feedback_1774924928825')
          .select('*')
          .order('created_at', { ascending: false });
          
        if (error) throw error;
        
        setFeedbackData(data || []);
      } catch (error) {
        console.error("Error fetching feedback:", error);
      } finally {
        setIsLoading(false);
      }
    }
    
    fetchFeedback();
  }, []);

  // Only display the dashboard when there is real data in the database
  if (isLoading || feedbackData.length === 0) {
    return null;
  }

  // Calculate metrics
  const totalReviews = feedbackData.length;
  const averageRating = (feedbackData.reduce((acc, curr) => acc + curr.rating, 0) / totalReviews).toFixed(1);
  
  // Calculate rating distribution
  const ratingCounts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  feedbackData.forEach(item => {
    if (ratingCounts[item.rating] !== undefined) {
      ratingCounts[item.rating]++;
    }
  });
  
  const ratingData = [5, 4, 3, 2, 1].map(stars => ({
    stars,
    count: ratingCounts[stars],
    percentage: Math.round((ratingCounts[stars] / totalReviews) * 100) || 0
  }));

  const positiveSentiment = Math.round(((ratingCounts[5] + ratingCounts[4]) / totalReviews) * 100) || 0;

  // MoM Engagement Calculation
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  
  const currentMonthReviews = feedbackData.filter(item => {
    const d = new Date(item.created_at);
    return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
  }).length;
  
  const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
  const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;
  
  const lastMonthReviews = feedbackData.filter(item => {
    const d = new Date(item.created_at);
    return d.getMonth() === lastMonth && d.getFullYear() === lastMonthYear;
  }).length;
  
  let momEngagement = 0;
  if (lastMonthReviews > 0) {
    momEngagement = Math.round(((currentMonthReviews - lastMonthReviews) / lastMonthReviews) * 100);
  } else if (currentMonthReviews > 0) {
    momEngagement = 100;
  }
  const momEngagementText = momEngagement >= 0 ? `+${momEngagement}%` : `${momEngagement}%`;

  // Use top recent reviews (max 3)
  const recentReviews = feedbackData.slice(0, 3).map(item => ({
    name: item.name,
    event: item.event_name,
    rating: item.rating,
    title: item.title,
    review: item.review,
    image: item.image_url || "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=256&q=80"
  }));

  return (
    <Section 
      id="analytics" 
      eyebrow="Analytics" 
      title="Feedback Analysis Dashboard" 
      subtitle="A transparent, data-driven look at the impact and reception of Dr. Jeff's engagements, workshops, and keynotes."
    >
      <div className="flex flex-col gap-6">
        
        {/* Top KPI Cards */}
        <motion.div 
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          <div className="rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur-md">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-500/20 text-orange-400 mb-4">
              <SafeIcon icon={FiStar} className="h-5 w-5" />
            </div>
            <div className="text-3xl font-bold text-white">{averageRating}</div>
            <div className="text-sm text-white/50 mt-1">Average Rating</div>
          </div>
          
          <div className="rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur-md">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500/20 text-blue-400 mb-4">
              <SafeIcon icon={FiUsers} className="h-5 w-5" />
            </div>
            <div className="text-3xl font-bold text-white">{totalReviews}</div>
            <div className="text-sm text-white/50 mt-1">Total Reviews</div>
          </div>

          <div className="rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur-md">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-500/20 text-purple-400 mb-4">
              <SafeIcon icon={FiSmile} className="h-5 w-5" />
            </div>
            <div className="text-3xl font-bold text-white">{positiveSentiment}%</div>
            <div className="text-sm text-white/50 mt-1">Positive Sentiment</div>
          </div>

          <div className="rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur-md">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-500/20 text-green-400 mb-4">
              <SafeIcon icon={FiTrendingUp} className="h-5 w-5" />
            </div>
            <div className="text-3xl font-bold text-white">{momEngagementText}</div>
            <div className="text-sm text-white/50 mt-1">MoM Engagement</div>
          </div>
        </motion.div>

        <div className="grid gap-6 lg:grid-cols-[1fr_1.5fr]">
          {/* Left Column: Breakdown & Keywords */}
          <motion.div 
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="flex flex-col gap-6"
          >
            {/* Rating Breakdown */}
            <div className="rounded-3xl border border-white/10 bg-white/5 p-6 md:p-8 backdrop-blur-md">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <SafeIcon icon={FiBarChart2} className="text-blue-400" /> Rating Distribution
              </h3>
              <div className="flex flex-col gap-4">
                {ratingData.map((data) => (
                  <div key={data.stars} className="flex items-center gap-4">
                    <div className="flex items-center gap-1 w-12 text-sm text-white/70">
                      {data.stars} <SafeIcon icon={FiStar} className="h-3 w-3 fill-white/50" />
                    </div>
                    <div className="flex-1 h-3 rounded-full bg-white/10 overflow-hidden">
                      <motion.div 
                        initial={{ width: 0 }}
                        whileInView={{ width: `${data.percentage}%` }}
                        transition={{ duration: 1, delay: 0.2 }}
                        viewport={{ once: true }}
                        className="h-full rounded-full bg-gradient-to-r from-orange-400 to-orange-300"
                      />
                    </div>
                    <div className="w-10 text-right text-sm text-white/50">
                      {data.percentage}%
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Keywords */}
            <div className="rounded-3xl border border-white/10 bg-white/5 p-6 md:p-8 backdrop-blur-md">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <SafeIcon icon={FiPieChart} className="text-purple-400" /> Top Mentioned Themes
              </h3>
              <div className="flex flex-wrap gap-3">
                {KEYWORDS.map((kw, i) => (
                  <span 
                    key={i} 
                    className={`px-4 py-2 rounded-full text-sm font-medium border ${
                      kw.weight === 'high' 
                        ? 'bg-purple-500/20 text-purple-300 border-purple-500/30' 
                        : kw.weight === 'medium'
                        ? 'bg-blue-500/20 text-blue-300 border-blue-500/30'
                        : 'bg-white/5 text-white/70 border-white/10'
                    }`}
                  >
                    {kw.text}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Right Column: Recent Reviews */}
          <motion.div 
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="flex flex-col gap-4"
          >
            <div className="rounded-3xl border border-white/10 bg-[linear-gradient(135deg,rgba(59,130,246,0.05),rgba(124,58,237,0.05))] p-6 md:p-8 backdrop-blur-md h-full flex flex-col">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <SafeIcon icon={FiMessageSquare} className="text-orange-400" /> Recent Highlights
              </h3>
              
              <div className="flex flex-col gap-4 flex-1">
                {recentReviews.map((review, i) => (
                  <motion.div 
                    key={i}
                    custom={i}
                    variants={fadeUp}
                    className="rounded-2xl border border-white/5 bg-white/[0.02] p-5 transition-all hover:bg-white/[0.04]"
                  >
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="flex items-center gap-3">
                        <img 
                          src={review.image} 
                          alt={review.name} 
                          className="w-10 h-10 rounded-full object-cover border border-white/20"
                        />
                        <div>
                          <h4 className="text-sm font-semibold text-white">{review.name}</h4>
                          <p className="text-xs text-white/50">{review.event}</p>
                        </div>
                      </div>
                      <div className="flex gap-0.5">
                        {[...Array(5)].map((_, index) => (
                          <SafeIcon 
                            key={index}
                            icon={FiStar} 
                            className={`h-3 w-3 ${index < review.rating ? "fill-orange-400 text-orange-400" : "text-white/20"}`} 
                          />
                        ))}
                      </div>
                    </div>
                    <h5 className="text-base font-medium text-white mb-1.5">"{review.title}"</h5>
                    <p className="text-white/70 leading-relaxed text-sm line-clamp-3">
                      {review.review}
                    </p>
                  </motion.div>
                ))}
              </div>
              
              <button className="mt-6 w-full rounded-xl border border-white/10 bg-white/5 px-6 py-3 text-sm font-medium text-white hover:bg-white/10 transition-colors flex items-center justify-center gap-2">
                View Full Access Dashboard
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </Section>
  );
}